import PropTypes from 'prop-types';
import * as React from 'react';
import type ColumnSynchronizer, {ColumnChangeEvent} from './ColumnSynchronizer';
import type {ColumnPropType} from './propTypes';
import {columnPropType, columnSynchronizerPropType} from './propTypes';
import {getCellClassName, getCellStyle} from './utils';

export type Props = {
    column: ColumnPropType,
    columnSynchronizer?: ColumnSynchronizer,
    children?: React.ReactNode,
};

export type State = {
    width?: number,
}

export default class DataTableHeaderCell extends React.PureComponent<Props, State> {

    static propTypes = {
        column: columnPropType.isRequired,
        columnSynchronizer: columnSynchronizerPropType,
        children: PropTypes.node,
    };

    props: Props;
    state: State = {};

    componentDidMount(): void {
        this.addColumnListenerIfNeeded();
    }

    componentDidUpdate(prevProps: Props): void {
        const {column, columnSynchronizer} = this.props;
        if (prevProps.columnSynchronizer !== columnSynchronizer) {
            this.removeColumnListenerIfNeeded(prevProps);
            this.addColumnListenerIfNeeded();
        } else if (columnSynchronizer && prevProps.column.name !== column.name) {
            this.setState({width: columnSynchronizer.getWidth(column.name)});
        }
    }

    componentWillUnmount(): void {
        this.removeColumnListenerIfNeeded();
    }

    addColumnListenerIfNeeded(): void {
        const {column, columnSynchronizer} = this.props;
        if (columnSynchronizer) {
            columnSynchronizer.addListener(this.handleColumnChange);
            this.setState({width: columnSynchronizer.getWidth(column.name)});
        }
    }

    removeColumnListenerIfNeeded(props: Props = this.props): void {
        const {columnSynchronizer} = props;
        if (columnSynchronizer) {
            columnSynchronizer.removeListener(this.handleColumnChange);
        }
    }

    handleColumnChange = (event: ColumnChangeEvent): void => {
        const {name, width} = event;
        if (this.props.column.name === name) {
            this.setState({width});
        }
    };

    render(): React.ReactNode {
        const {
            style: baseStyle,
            className: baseClassName,
            column,
            columnSynchronizer,
            children,
            ...otherProps
        } = this.props;

        const {width} = this.state;
        const style = getCellStyle(column, baseStyle, width);
        const className = getCellClassName(column, baseClassName);

        return (
            <th className={className} style={style} {...otherProps}>
                {children}
            </th>
        );
    }

}
